import {FC, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {useNavigate} from 'react-router-dom'
import {ExpansionPlantCharts, ExpansionPlantItem} from '../../../../@types/expansionPortfolio'
import {useAuth} from '../../../../modules/auth'
import {getPlantExpansionCharts, getplantsOne} from '../../../../services/plantsService'
import {getContrastColor} from '../../../../utils/contratColor'
import {ModalAddEditV2} from '../../../proposalGenerator/components/ModalAddEditV2'
import {
  buildExpansionProposalDraft,
  buildSimpleExpansionSuggestion,
  EXPANSION_MOCK,
  formatCurrency,
  formatExpansionTariffHint,
  formatKwh,
  formatKwp,
  formatNumber,
  getStatusBadgeClass,
  simulateFromPlant,
} from '../../utils'
import styles from '../../expansionPortfolio.module.css'

type Props = {
  plant: ExpansionPlantItem | null
  months: number
  defaultTargetPercentage: number
}

const MockBadge: FC = () => <span className={styles.mockBadge}>mock</span>

const ExpansionPlantDetail: FC<Props> = ({plant, months, defaultTargetPercentage}) => {
  const navigate = useNavigate()
  const [targetPercentage, setTargetPercentage] = useState(defaultTargetPercentage)
  const [showProposalModal, setShowProposalModal] = useState(false)
  const [proposalDraft, setProposalDraft] = useState<any | null>(null)
  const [openingProposal, setOpeningProposal] = useState(false)
  const {currentUser} = useAuth()

  const primaryColor = currentUser?.companies?.[0]?.color_primary
  const savingsTextColor = primaryColor ? getContrastColor(primaryColor) : undefined

  const generationKwh = plant
    ? plant.totalGenerationKwh ?? plant.avgGenerationKwh ?? 0
    : 0
  const consumptionKwh = plant
    ? plant.totalConsumptionKwh ?? plant.avgConsumptionKwh ?? 0
    : 0

  const {
    data: chartsData,
    isLoading: chartsLoading,
    isFetching: chartsFetching,
    isError: chartsError,
  } = useQuery<ExpansionPlantCharts>(
    ['expansion-plant-charts', plant?.id, months],
    () => getPlantExpansionCharts(plant!.id, months),
    {
      enabled: Boolean(plant?.id),
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  )

  const chartsBusy = chartsLoading || chartsFetching

  const peakGenerationKw = chartsData?.peakGenerationKw ?? null
  const averageEfficiencyPercent = chartsData?.averageEfficiencyPercent ?? null
  const peakDate = chartsData?.peakDate ?? null

  const tariffFromInvoice =
    chartsData?.tariffSource === 'invoice' &&
    chartsData?.tariffPerKwh != null &&
    chartsData.tariffPerKwh > 0
      ? chartsData.tariffPerKwh
      : null
  const tariffPerKwh = tariffFromInvoice ?? EXPANSION_MOCK.tariffPerKwh
  const isTariffMock = tariffFromInvoice === null
  const tariffHint = formatExpansionTariffHint({
    tariffPerKwh,
    isTariffMock,
    tariffGroup: chartsData?.tariffGroup,
    tariffPonta: chartsData?.tariffPonta,
    tariffForaPonta: chartsData?.tariffForaPonta,
    tariffSharePonta: chartsData?.tariffSharePonta,
  })
  const tariffHintCompact = formatExpansionTariffHint({
    tariffPerKwh,
    isTariffMock,
    tariffGroup: chartsData?.tariffGroup,
    tariffPonta: chartsData?.tariffPonta,
    tariffForaPonta: chartsData?.tariffForaPonta,
    tariffSharePonta: chartsData?.tariffSharePonta,
    compact: true,
  })

  const isGrupoA =
    chartsData?.tariffGroup === 'A' &&
    ((chartsData?.tariffPonta != null && chartsData.tariffPonta > 0) ||
      (chartsData?.tariffForaPonta != null && chartsData.tariffForaPonta > 0))
  const economyTariffLabel = isGrupoA ? 'kWh × tarifa média' : 'kWh × tarifa'

  const tariffParams = {
    tariffPerKwh,
    tariffGroup: chartsData?.tariffGroup ?? null,
    tariffPonta: chartsData?.tariffPonta ?? null,
    tariffForaPonta: chartsData?.tariffForaPonta ?? null,
    tariffSharePonta: chartsData?.tariffSharePonta ?? null,
  }

  const simulation = useMemo(() => {
    if (!plant) return null
    return simulateFromPlant(
      generationKwh,
      consumptionKwh,
      plant.capacityKwp,
      targetPercentage,
      tariffParams
    )
  }, [
    plant,
    generationKwh,
    consumptionKwh,
    targetPercentage,
    tariffPerKwh,
    chartsData?.tariffGroup,
    chartsData?.tariffPonta,
    chartsData?.tariffForaPonta,
    chartsData?.tariffSharePonta,
  ])

  const suggestion = useMemo(() => {
    if (!plant) return null
    return buildSimpleExpansionSuggestion({
      generationKwh,
      consumptionKwh,
      capacityKwp: plant.capacityKwp,
      targetPercentage,
      ...tariffParams,
      months,
    })
  }, [
    plant,
    generationKwh,
    consumptionKwh,
    targetPercentage,
    tariffPerKwh,
    chartsData?.tariffGroup,
    chartsData?.tariffPonta,
    chartsData?.tariffForaPonta,
    chartsData?.tariffSharePonta,
    months,
  ])

  if (!plant) {
    return (
      <div className={styles.detailCard}>
        <div className={styles.emptyState}>
          <span className='fs-5 fw-bold text-gray-800 mb-2'>Selecione uma usina</span>
          <span className='fs-7'>
            Escolha um ativo na lista à esquerda para ver o diagnóstico de expansão.
          </span>
        </div>
      </div>
    )
  }

  const energyScale = Math.max(generationKwh, consumptionKwh, 0)
  const generationBarPercent =
    energyScale > 0 ? Math.min(100, (generationKwh / energyScale) * 100) : 0
  const consumptionBarPercent =
    energyScale > 0 ? Math.min(100, (consumptionKwh / energyScale) * 100) : 0

  const insight =
    plant.coveragePercent !== null
      ? `Nos últimos ${months} meses, a geração total cobriu ${formatNumber(
          plant.coveragePercent,
          1
        )}% do consumo total do período${
          plant.deficitKwh > 0
            ? `, com déficit de ${formatNumber(plant.deficitKwh, 0)} kWh.`
            : '.'
        }`
      : 'Ainda não há dados suficientes de consumo nos relatórios para calcular o déficit desta usina.'

  const isOperational = plant.status === 'OPERACIONAL'
  const censored = '—'

  const openExpansionProposal = async () => {
    if (!plant || !suggestion || openingProposal) return

    try {
      setOpeningProposal(true)
      let idUnitBranch: string | null = null
      let idClient: string | null = null

      try {
        const plantDetails = await getplantsOne(plant.id)
        idUnitBranch = plantDetails?.unitBranch?.id ?? plantDetails?.unitBranch_id ?? null
        idClient = plantDetails?.client?.id ?? plantDetails?.client_id ?? null
      } catch {
        // Mantém draft sem filial/cliente — usuário seleciona no modal
      }

      const draft = buildExpansionProposalDraft({
        plant,
        months,
        targetPercentage,
        suggestion,
        simulation,
        generationKwh,
        consumptionKwh,
        tariffHint,
        idUnitBranch,
        idClient,
      })

      setProposalDraft(draft)
      setShowProposalModal(true)
    } finally {
      setOpeningProposal(false)
    }
  }

  return (
    <div className={styles.detailCard}>
      <div className={styles.detailHeader}>
        <div>
          <div className='fs-3 fw-bolder text-gray-900 mb-1'>Análise de Ampliação de Ativos</div>
          <div className='d-flex gap-2'>
            <h2 className='text-muted fs-6 fw-semibold'>{plant.name}</h2>
            <div className='text-muted fs-7'>
              {` · `}
              {plant.addressReduce || plant.address || plant.location || 'Localização não informada'}
              {plant.capacityKwp > 0 ? ` · ${formatKwp(plant.capacityKwp)}` : ''}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          {/* <button type='button' className='btn btn-sm btn-light' disabled>
            Comparar Histórico
          </button> */}
          <button
            type='button'
            className='btn btn-sm btn-primary'
            disabled={!suggestion || openingProposal}
            onClick={openExpansionProposal}
            title='Gerar proposta com dados do cenário atual'
          >
            {openingProposal ? (
              <span className='d-inline-flex align-items-center gap-2'>
                <span className='spinner-border spinner-border-sm' />
                Preparando...
              </span>
            ) : (
              'Gerar Proposta de Ampliação'
            )}
          </button>
        </div>
      </div>

      <div className={styles.detailBody}>
        <div className={styles.heroStatus}>
          <div className='d-flex align-items-center gap-2 mb-2'>
            <span className='fs-8 fw-bold text-uppercase opacity-75'>Status</span>
            <span className={`badge ${getStatusBadgeClass(plant.status)}`}>{plant.statusLabel}</span>
          </div>
          <p className='mb-0 fs-6 opacity-90'>{insight}</p>

          <div className={styles.heroGrid}>
            <div className={styles.deficitBox}>
              <div className='fs-8 text-uppercase fw-bold opacity-75 mb-1'>Déficit energético</div>
              <div className={styles.deficitValue}>{formatNumber(plant.deficitKwh, 0)}</div>
              <div className='fs-7 opacity-75'>kWh no período</div>
            </div>
            <div className={styles.deficitBox}>
              <div className='fs-8 text-uppercase fw-bold opacity-75 mb-1'>Demanda atendida</div>
              <div className={styles.deficitValue}>
                {plant.coveragePercent !== null ? `${formatNumber(plant.coveragePercent, 0)}%` : '—'}
              </div>
              <div className='fs-7 opacity-75'>
                {plant.monthsWithData > 0
                  ? `Soma de ${plant.monthsWithData} mês(es)`
                  : 'Sem histórico no período'}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className='fs-6 fw-bold text-gray-900 mb-3'>Balanço energético</div>

            <div className={styles.metricRow}>
              <span className='text-muted fs-7'>Geração total</span>
              <span className='fw-bold'>{formatKwh(generationKwh)}</span>
            </div>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{width: `${generationBarPercent}%`}} />
            </div>

            <div className={`${styles.metricRow} mt-3`}>
              <span className='text-muted fs-7'>Consumo total</span>
              <span className='fw-bold'>{formatKwh(consumptionKwh)}</span>
            </div>
            <div className={styles.progressTrack}>
              <div
                className={`${styles.progressFill} ${styles.progressFillDanger}`}
                style={{width: `${consumptionBarPercent}%`}}
              />
            </div>

            <div className={styles.statPair}>
              <div className={styles.statBox}>
                <div className={styles.statLabel}>Potência Instalada</div>
                <div className={styles.statValue}>
                  {plant.capacityKwp > 0 ? formatKwp(plant.capacityKwp) : '—'}
                </div>
              </div>
              <div className={styles.statBox}>
                <div className={styles.statLabel}>Pico de Geração</div>
                {chartsBusy ? (
                  <div className='d-flex align-items-center gap-2 mt-1'>
                    <span className='spinner-border spinner-border-sm text-primary' />
                    <span className='text-muted fs-8'>Carregando...</span>
                  </div>
                ) : peakGenerationKw !== null ? (
                  <>
                    <div className={styles.statValue}>
                      {formatNumber(peakGenerationKw, 1)} kW
                    </div>
                    {/* {peakDate && <div className='text-muted fs-9 mt-1'>em {peakDate}</div>} */}
                  </>
                ) : (
                  <>
                    <div className={styles.statValue}>
                      {formatNumber(EXPANSION_MOCK.peakGenerationKw, 0)} kW
                    </div>
                    <div className='d-flex align-items-center gap-1 mt-1'>
                      {/* <MockBadge /> */}
                      <span className='text-muted fs-9'>
                        {chartsError ? 'falha ao buscar gráfico' : 'sem dados no período'}
                      </span>
                    </div>
                  </>
                )}
              </div>



              <div className={styles.statBox}>
                <div className={styles.statLabel}>Eficiência Média</div>
                {chartsBusy ? (
                  <div className='d-flex align-items-center gap-2 mt-1'>
                    <span className='spinner-border spinner-border-sm text-primary' />
                    <span className='text-muted fs-8'>Carregando...</span>
                  </div>
                ) : averageEfficiencyPercent !== null ? (
                  <div className={styles.statValue}>
                    {formatNumber(averageEfficiencyPercent, 1)}%
                  </div>
                ) : (
                  <>
                    <div className={styles.statValue}>
                      {formatNumber(EXPANSION_MOCK.averageEfficiencyPercent, 1)}%
                    </div>
                    <div className='d-flex align-items-center gap-1 mt-1'>
                      {/* <MockBadge /> */}
                      <span className='text-muted fs-9'>
                        {chartsError ? 'falha ao buscar gráfico' : 'sem pico ou capacidade'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={styles.metricCard}>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div className='fs-6 fw-bold text-gray-900'>Simular cenários</div>
              <span className='badge badge-light-primary'>{targetPercentage}%</span>
            </div>

            <label className='form-label fs-8 fw-bold text-muted text-uppercase'>
              Meta de autossuficiência
            </label>
            <input
              type='range'
              className='form-range'
              min={80}
              max={150}
              step={1}
              value={targetPercentage}
              onChange={(e) => setTargetPercentage(Number(e.target.value))}
            />
            <div className={styles.sliderLabels}>
              <span>80%</span>
              <span>100% break-even</span>
              <span>150%</span>
            </div>

            <div className='separator my-4' />

            <div className={styles.simStatStack}>
              <div className={styles.simStatCard}>
                <span className={styles.simStatLabel}>Excedente projetado</span>
                <span className={styles.simStatValue}>
                  {simulation
                    ? `${formatNumber(simulation.surplusKwh, 0)} kWh / ${months} meses`
                    : '—'}
                </span>
              </div>

              <div className={styles.simStatCard}>
                <span className={styles.simStatLabel}>
                  Créditos de energia {isTariffMock && !chartsBusy && <MockBadge />}
                </span>
                {chartsBusy ? (
                  <span className='d-flex align-items-center gap-2'>
                    <span className='spinner-border spinner-border-sm text-primary' />
                    <span className='text-muted fs-8'>Carregando...</span>
                  </span>
                ) : (
                  <span className={styles.simStatValue}>
                    {simulation ? formatCurrency(simulation.creditsReais) : '—'}
                  </span>
                )}
              </div>

              <div className={styles.simStatCard}>
                <span className={styles.simStatLabel}>kWp necessário</span>
                <span className={`${styles.simStatValue} text-primary`}>
                  {simulation && simulation.suggestedExpansionKwp > 0
                    ? formatKwp(simulation.suggestedExpansionKwp)
                    : '—'}
                </span>
              </div>
            </div>

            <div className='text-muted fs-9 mt-3'>
              {chartsBusy ? (
                <span className='d-flex align-items-center gap-2'>
                  <span className='spinner-border spinner-border-sm text-primary' />
                  <span>Carregando tarifa...</span>
                </span>
              ) : (
                <>*Créditos com {tariffHint}.</>
              )}
            </div>
          </div>
        </div>

        {suggestion && (
          <div className={styles.expansionSection}>
            <div className={styles.suggestionCard}>
              <div className='fs-6 fw-bold text-gray-900'>Sugestão de Expansão</div>
              <div className='text-muted fs-8 mt-1'>
                Meta de {targetPercentage}% · economia ={' '}
                {targetPercentage <= 100
                  ? `déficit até ${targetPercentage}%`
                  : 'déficit até 100% + excedente da meta (100%)'}{' '}
                × {isGrupoA ? 'tarifa média' : 'tarifa'}
              </div>

              <div className={styles.suggestionMetricsStack}>
                <div className={styles.suggestionMetric}>
                  <div className={styles.suggestionMetricLabel}>
                    <span className='bi bi-lightning-charge' />
                    Potência extra
                  </div>
                  <div
                    className={`${styles.suggestionMetricValueCompact} text-primary`}
                    style={primaryColor ? {color: primaryColor} : undefined}
                  >
                    {isOperational
                      ? censored
                      : suggestion.extraPowerKwp > 0
                        ? formatKwp(suggestion.extraPowerKwp)
                        : '—'}
                  </div>
                </div>

                {suggestion.deficitTo100Kwh > 0 && (
                  <div className={styles.suggestionMetricCompact}>
                    <div className={styles.suggestionMetricLabel}>
                      <span className='bi bi-battery-half' />
                      Até {Math.min(targetPercentage, 100)}%
                    </div>
                    <div className={styles.suggestionMetricValueCompact}>
                      {isOperational
                        ? censored
                        : `${formatNumber(suggestion.deficitTo100Kwh, 0)} kWh`}
                    </div>
                  </div>
                )}

                {suggestion.surplusBeyond100Kwh > 0 && (
                  <div className={styles.suggestionMetricCompact}>
                    <div className={styles.suggestionMetricLabel}>
                      <span className='bi bi-graph-up-arrow' />
                      Excedente da meta (100%)
                    </div>
                    <div className={styles.suggestionMetricValueCompact}>
                      {isOperational
                        ? censored
                        : `${formatNumber(suggestion.surplusBeyond100Kwh, 0)} kWh`}
                    </div>
                  </div>
                )}

                {/* <div className={styles.suggestionMetricCompact}>
                  <div className={styles.suggestionMetricLabel}>
                    <span className='bi bi-cash-coin' />
                    Tarifa {isTariffMock ? 'padrão' : 'da fatura'}
                    {chartsBusy ? ' (carregando...)' : ''}
                  </div>
                  <div className={styles.suggestionMetricValueCompact}>
                    R$ {tariffPerKwh.toFixed(4).replace('.', ',')}/kWh
                  </div>
                </div> */}

                {/* {suggestion.annualEconomyReais > 0 && (
                  <div className={styles.suggestionMetricCompact}>
                    <div className={styles.suggestionMetricLabel}>
                      <span className='bi bi-calendar3' />
                      Projeção anual
                    </div>
                    <div className={styles.suggestionMetricValueCompact}>
                      ≈ {formatCurrency(suggestion.annualEconomyReais)}
                    </div>
                  </div>
                )} */}

                {suggestion.deficitTo100Kwh <= 0 &&
                  suggestion.surplusBeyond100Kwh <= 0 &&
                  suggestion.economyKwh <= 0 && (
                    <div className={styles.suggestionMetricCompact}>
                      <div className={styles.suggestionMetricLabel}>
                        <span className='bi bi-check-circle' />
                        Situação
                      </div>
                      <div className={styles.suggestionMetricValueCompact}>
                        {isOperational ? censored : 'Sem gap energético no período'}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <div className={styles.savingsSide}>
              <div className={styles.savingsPanel}>
                <div
                  className={styles.savingsHero}
                  style={{
                    ...(primaryColor ? {backgroundColor: primaryColor} : {}),
                    ...(savingsTextColor ? {color: savingsTextColor} : {}),
                  }}
                >
                  <div className='fs-8 fw-bold text-uppercase opacity-75 d-flex align-items-center gap-2'>
                    Economia no período
                    {isTariffMock && !isOperational ? <MockBadge /> : null}
                  </div>
                  <div className={styles.savingsValue}>
                    {isOperational
                      ? censored
                      : suggestion.economyReais > 0
                        ? formatCurrency(suggestion.economyReais)
                        : '—'}
                  </div>
                  <div className={styles.savingsSubtitle}>
                    {isOperational
                      ? censored
                      : suggestion.economyKwh > 0
                        ? `${economyTariffLabel}`
                        : 'Sem economia projetada para a meta atual'}
                  </div>
                </div>

                <div className={styles.savingsStats}>
                  <div className={styles.savingsStat}>
                    <div className={styles.savingsStatLabel}>Meta</div>
                    <div className={styles.savingsStatValue}>{targetPercentage}%</div>
                  </div>
                  <div className={styles.savingsStat}>
                    <div className={styles.savingsStatLabel}>
                      {isGrupoA ? 'Tarifa Média' : 'Tarifa'} {isTariffMock && !isOperational ? 'padrão' : ''}
                    </div>
                    <div className={styles.savingsStatValue}>
                      {isOperational && isTariffMock
                        ? censored
                        : `R$ ${tariffPerKwh.toFixed(4).replace('.', ',')}`}
                    </div>
                    {!isOperational && !chartsBusy && (
                      <div className='text-muted fs-9 mt-1'>{tariffHintCompact}</div>
                    )}
                  </div>
                  <div className={styles.savingsStat}>
                    <div className={styles.savingsStatLabel}>Energia</div>
                    <div className={styles.savingsStatValue}>
                      {isOperational
                        ? censored
                        : suggestion.economyKwh > 0
                          ? `${formatNumber(suggestion.economyKwh, 0)} kWh`
                          : '—'}
                    </div>
                    {!isOperational && (
                      <div className='text-muted fs-9 mt-1'>
                        {targetPercentage <= 100
                          ? `Déficit até ${targetPercentage}%`
                          : 'Déficit até 100% + excedente da meta'}
                      </div>
                    )}
                  </div>
                  <div className={styles.savingsStat}>
                    <div className={styles.savingsStatLabel}>Anual</div>
                    <div
                      className={styles.savingsStatValue}
                      style={primaryColor ? {color: primaryColor} : undefined}
                    >
                      {isOperational
                        ? censored
                        : suggestion.annualEconomyReais > 0
                          ? formatCurrency(suggestion.annualEconomyReais)
                          : '—'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Versão completa (painéis, investimento, ROI, próximos passos) desativada — ver buildExpansionSuggestion em utils.ts */}
      </div>

      {showProposalModal && proposalDraft && (
        <ModalAddEditV2
          show={showProposalModal}
          id={proposalDraft?.id}
          data={proposalDraft}
          setMyProposal={setProposalDraft}
          handleClose={() => {
            setShowProposalModal(false)
            setProposalDraft(null)
            navigate('/proposalGenerator')
          }}
        />
      )}
    </div>
  )
}

export {ExpansionPlantDetail}
